import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IndianRupee, PenLine, Globe, Smartphone, ChevronRight, ImageUp, X, FileSpreadsheet, Ban, AlertTriangle, CheckCircle2, OctagonAlert, Receipt, SlidersHorizontal, Layers } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "./ui/drawer";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { Textarea } from "./ui/textarea";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import { api, errMsg, fmtINR } from "../lib/api";
import { pokeLiveCollections } from "../lib/liveCollections";
import { compressToDataUrl } from "../lib/imageCompress";
import { PaymentRecordedDialog } from "./ReceiptVisibilityNotice";   // Phase 169
import { TaxHeadPicker, initialSelection, summarizeSelection, lineMax } from "./TaxHeadPicker";   // Phase 229

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const CHANNELS = [
  { key: "manual", title: "Manual collection", desc: "Record cash / cheque / UPI collected offline", icon: PenLine, testId: "channel-manual" },
  { key: "online", title: "Online payment (gateway)", desc: "Open the agency payment gateway on this device", icon: Globe, testId: "channel-online" },
  { key: "pos", title: "PoS device", desc: "Card / UPI collected on the handheld PoS machine", icon: Smartphone, testId: "channel-pos" },
];

/** Phase 190 — reasons a collector may give for collecting MORE than the amount payable today. */
export const EXCESS_REASONS = [
  { value: "wrong_demand", label: "Wrong Demand", hint: "The demand on record looks wrong. The payment is flagged for review — the Supervisor can comment, the Municipal Admin / Super Admin corrects the demand and the excess is settled against it automatically." },
  { value: "advance_payment", label: "Advance Payment", hint: "The owner is paying ahead. The excess is held as advance credit against this property and is applied automatically to the next demand." },
  { value: "other", label: "Other", hint: "Write what happened (mandatory). The payment is flagged for admin review." },
];

/** Phase 190 — compare the amount handed over with the amount payable today (one-paisa tolerance). */
export const classifyAmount = (amount, payable) => {
  const a = Math.round((Number(amount) || 0) * 100) / 100;
  const p = Math.round((Number(payable) || 0) * 100) / 100;
  if (a <= 0) return { kind: "none", excess: 0, remaining: p };
  if (a > p + 0.009) return { kind: "excess", excess: Math.round((a - p) * 100) / 100, remaining: 0 };
  if (a < p - 0.009) return { kind: "partial", excess: 0, remaining: Math.round((p - a) * 100) / 100 };
  return { kind: "full", excess: 0, remaining: 0 };
};

const EMPTY_EXTRA = {
  cheque_number: "", cheque_date: "", cheque_collection_date: "", dd_number: "",
  bank_name: "", transaction_ref: "", utr_number: "", pos_device_id: "",
};

/**
 * Phase 177 — does a bulk-imported payment exist for this PIN? `check.blocked` → the import already covers the full
 * outstanding (all FYs) and manual entry is not allowed; `check.has_import` → the typed amount is capped to the
 * remaining balance (Phase 195: nothing is pre-filled). No import → the normal flow. Re-checked whenever the property (or its ledger) changes.
 */
export const useImportCheck = (code, propertyId, reloadKey = 0) => {
  const [check, setCheck] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!code || !propertyId) { setCheck(null); return undefined; }
    let alive = true;
    setLoading(true);
    api.get(`/t/${code}/payments/import-check`, { params: { property_id: propertyId } })
      .then((r) => { if (alive) setCheck(r.data); })
      .catch(() => { if (alive) setCheck(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [code, propertyId, reloadKey]);
  return { check, loading, blocked: !!check?.blocked, hasImport: !!check?.has_import };
};

/** Banner shown above the Collect button: red when manual entry is blocked, amber when the amount is capped. */
export const ImportBlockNotice = ({ check, testId = "import-check-notice" }) => {
  if (!check?.has_import) return null;
  if (check.blocked) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-sm text-destructive" role="alert" data-testid={`${testId}-blocked`}>
        <Ban className="h-4 w-4 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="font-medium" data-testid={`${testId}-message`}>{check.message}</p>
          <p className="text-[11px] mt-0.5 opacity-90">
            {check.imported_count} imported receipt{check.imported_count === 1 ? "" : "s"} totalling {fmtINR(check.imported_total)} already cover everything due on this property.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50/80 px-3 py-2.5 text-sm text-amber-900" role="status" data-testid={`${testId}-capped`}>
      <FileSpreadsheet className="h-4 w-4 shrink-0 mt-0.5" />
      <div className="min-w-0">
        <p className="font-medium" data-testid={`${testId}-message`}>{check.message}</p>
        <p className="text-[11px] mt-0.5">
          Bulk import on file: receipt {check.latest_receipt} · {fmtINR(check.latest_amount)} on {check.latest_date}
          {check.imported_count > 1 ? ` (+${check.imported_count - 1} more, ${fmtINR(check.imported_total)} in total)` : ""}. The amount you enter below cannot exceed the remaining balance of {fmtINR(check.remaining_payable)}.
        </p>
      </div>
    </div>
  );
};

/** `gate` (Phase 134): optional guard — called with the open action; it decides whether to run it now
 *  (owner mobile verified today) or first take the user through the verification step.
 *  `importCheck` (Phase 177): result of useImportCheck — blocks the button / caps the amounts when a bulk import exists. */
export const CollectPaymentFlow = ({ code, property, pendingDemands, disabled, gate, importCheck }) => {
  const navigate = useNavigate();
  const [stage, setStage] = useState(null); // choose | manual | pos | online
  const [modes, setModes] = useState([]);
  const [quote, setQuote] = useState(null);
  const [allocations, setAllocations] = useState({});
  const [modeId, setModeId] = useState("");
  const [extra, setExtra] = useState(EMPTY_EXTRA);
  const [chequePhoto, setChequePhoto] = useState("");
  const [nextDate, setNextDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [onlineAmount, setOnlineAmount] = useState("");
  // Phase 169: when the municipality hides receipts for this role we confirm the payment here instead of opening the receipt
  const [recorded, setRecorded] = useState(null);
  // Phase 190: ONE "Amount collected" field drives the allocation (oldest demand first); the entered amount is checked
  // live against the amount payable today → partial (confirm), full, or excess (reason mandatory)
  const [amountStr, setAmountStr] = useState("");
  const [partialConfirmed, setPartialConfirmed] = useState(false);
  const [excessReason, setExcessReason] = useState("");
  const [excessComment, setExcessComment] = useState("");
  const [showAlloc, setShowAlloc] = useState(false);
  // Phase 229: several tax heads on the property -> the collector / supervisor first ticks the head(s) + year(s) being
  // paid with an amount per line (everything pre-ticked in full); only those demands are settled
  const [headSel, setHeadSel] = useState({});
  const [afterHeads, setAfterHeads] = useState("choose");   // where the heads step continues to
  const [headsLocked, setHeadsLocked] = useState(false);    // picker left a real selection -> amount box read-only

  useEffect(() => {
    api.get(`/t/${code}/masters/payment-modes`, { params: { include_inactive: false } })
      .then((r) => setModes(r.data)).catch(() => {});
  }, [code]);

  const qmap = useMemo(() => {
    const m = {};
    (quote?.demands || []).forEach((q) => { m[q.demand_id] = q; });
    return m;
  }, [quote]);

  const netPayableTotal = quote
    ? quote.totals.net_payable_full
    : pendingDemands.reduce((s, d) => s + d.balance_amount, 0);
  const headsMode = !!quote?.multi_head;                       // Phase 229
  const headLines = useMemo(() => quote?.demands || [], [quote]);
  const headCaps = importCheck?.has_import ? importCheck.per_demand_cap : undefined;
  const headSummary = useMemo(() => summarizeSelection(headLines, headSel, headCaps), [headLines, headSel, headCaps]);
  const everyLineFull = (sel) => headLines.length > 0 && headLines.every((ln) => sel[ln.demand_id]?.checked
    && Math.abs((Number(sel[ln.demand_id].amount) || 0) - lineMax(ln, headCaps)) < 0.005);
  // the amount box is typed into only when the picker left EVERY line ticked in full (then more = excess, less = FIFO
  // partial exactly as today); with a real selection the amount is the sum of the ticked lines and is changed via the picker
  const amountLocked = headsMode && headsLocked;
  const amount = Math.round((Number(amountStr) || 0) * 100) / 100;
  // Phase 195: the amount is whatever the collector TYPED — never the demand figure by default. A blank field means
  // nothing is collected yet (Submit stays disabled); the per-demand boxes only mirror the typed amount (FIFO).
  const hasAmount = amountStr.trim() !== "" && amount > 0;
  const totalPay = hasAmount ? amount : 0;
  const check = classifyAmount(totalPay, netPayableTotal);
  const isPartial = check.kind === "partial";
  const isExcess = check.kind === "excess";
  const outstandingAfter = Math.max(0, Math.round((netPayableTotal - totalPay) * 100) / 100);
  const selectedMode = modes.find((m) => m.id === modeId);
  const isCheque = selectedMode?.mode_code === "CHQ" || /cheque/i.test(selectedMode?.mode_name || "");
  const wantsTxnRef = ["UPI", "DC", "CC", "NB", "POS"].includes(selectedMode?.mode_code)
    || /upi|card|net\s*banking|pos/i.test(selectedMode?.mode_name || "");
  const excessOk = !isExcess || (!!excessReason && (excessReason !== "other" || excessComment.trim().length >= 5));
  const partialOk = !isPartial || (partialConfirmed && !!nextDate);
  // the collector must type an amount, actively confirm a partial payment / give a reason for an excess before Submit becomes active
  const canSubmit = hasAmount && excessOk && partialOk && !busy;

  const capFor = (d, q) => {   // Phase 177: with a bulk import on file the per-demand amount is capped to the remaining balance
    const base = q ? q.net_payable_full : d.balance_amount;
    const cap = importCheck?.has_import ? importCheck.per_demand_cap?.[d.id] : undefined;
    return cap != null ? Math.min(base, cap) : base;
  };
  const clampAlloc = (d, raw) => {
    if (!importCheck?.has_import || raw === "" || raw == null) return raw;
    const n = Number(raw);
    if (!Number.isFinite(n)) return raw;
    const max = capFor(d, qmap[d.id]);
    if (n > max + 0.004) { toast.warning(`Capped at ${fmtINR(max)} — a bulk import already covers the rest of this demand`); return String(max); }
    return raw;
  };

  /** Phase 190: spread `amt` over the pending demands oldest-first, each capped at its net payable; the rest is excess. */
  const fifoAllocate = (amt, q = quote) => {
    const qm = {};
    (q?.demands || []).forEach((x) => { qm[x.demand_id] = x; });
    let left = Math.round((Number(amt) || 0) * 100) / 100;
    const out = {};
    pendingDemands.forEach((d) => {
      const cap = capFor(d, qm[d.id]);
      const take = Math.max(0, Math.min(cap, left));
      out[d.id] = take > 0 ? Math.round(take * 100) / 100 : "";
      left = Math.round((left - take) * 100) / 100;
    });
    return out;
  };
  const onAmountChange = (raw) => {
    let v = raw;
    if (importCheck?.has_import && importCheck.remaining_payable != null && Number(raw) > importCheck.remaining_payable + 0.004) {
      toast.warning(`Only the remaining balance of ${fmtINR(importCheck.remaining_payable)} can be collected — a bulk import already covers the rest`);
      v = String(importCheck.remaining_payable);
    }
    setAmountStr(v);
    const fifo = fifoAllocate(v);
    setAllocations(fifo);
    if (headsMode) {   // Phase 229: typing is only possible with every line ticked in full - keep the picker in step
      const next = { ...headSel };
      headLines.forEach((ln) => { const a = fifo[ln.demand_id]; next[ln.demand_id] = { checked: Number(a) > 0, amount: Number(a) > 0 ? String(a) : "" }; });
      setHeadSel(next);
    }
    setPartialConfirmed(false);
  };
  const onAllocChange = (d, raw) => {   // advanced: editing one demand's share re-derives the total
    const next = { ...allocations, [d.id]: clampAlloc(d, raw) };
    setAllocations(next);
    const sum = Object.values(next).reduce((s, v) => s + (Number(v) || 0), 0);
    setAmountStr(sum > 0 ? String(Math.round(sum * 100) / 100) : "");
    setPartialConfirmed(false);
  };

  const open = async () => {
    if (importCheck?.blocked) { toast.error(importCheck.message); return; }
    // Phase 195: the manual / PoS amount starts BLANK — the collector types what was actually received. Only the online
    // gateway tab keeps the full net payable as its default (the gateway cannot carry an excess).
    let q = null;
    try {
      const r = await api.get(`/t/${code}/payments/quote`, { params: { property_id: property.id } });
      q = r.data;
      setQuote(q);
      const full = importCheck?.has_import && importCheck.remaining_payable != null
        ? Math.min(q.totals.net_payable_full, importCheck.remaining_payable) : q.totals.net_payable_full;
      setOnlineAmount(String(full));
    } catch (err) {
      setQuote(null);
      const full = pendingDemands.reduce((s, d) => s + d.balance_amount, 0);
      setOnlineAmount(String(full));
    }
    setAllocations({});
    setAmountStr("");
    setExtra(EMPTY_EXTRA);
    setChequePhoto("");
    setNextDate("");
    setModeId("");
    setPartialConfirmed(false);
    setExcessReason("");
    setExcessComment("");
    setShowAlloc(false);
    // Phase 229: more than one tax head on the property -> choose the head(s) / year(s) first (everything pre-ticked in
    // full); a single head goes straight to the channel chooser exactly as before
    if (q?.multi_head) {
      setHeadSel(initialSelection(q.demands, importCheck?.has_import ? importCheck.per_demand_cap : undefined));
      setHeadsLocked(false);
      setAfterHeads("choose");
      setStage("heads");
    } else {
      setHeadSel({});
      setHeadsLocked(false);
      setStage("choose");
    }
  };

  /** Phase 229: the heads step is done -> the ticked lines become the allocations and their sum the amount. */
  const applyHeads = () => {
    const s = summarizeSelection(headLines, headSel, headCaps);
    if (s.errors.length) return toast.error(s.errors[0]);
    if (s.selectedCount === 0) return toast.error("Tick at least one tax head to pay");
    const next = {};
    s.allocations.forEach((a) => { next[a.demand_id] = a.amount; });
    setAllocations(next);
    setAmountStr(String(s.total));
    setOnlineAmount(String(s.total));
    setHeadsLocked(!everyLineFull(headSel));
    setPartialConfirmed(false);
    setExcessReason("");
    setExcessComment("");
    setStage(afterHeads || "choose");
  };
  /** Phase 229: go back to the heads step from any later step and return to that step afterwards. */
  const changeHeads = (from) => { setAfterHeads(from); setStage("heads"); };
  const headsNote = headsMode ? (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2" data-testid="pay-heads-note">
      <div className="min-w-0 text-xs">
        <p className="font-medium inline-flex items-center gap-1.5"><Layers className="h-3.5 w-3.5 text-primary" /> Paying {headSummary.headsSelected} of {headSummary.headsTotal} tax heads
          {headSummary.lineCount > headSummary.headsTotal ? ` · ${headSummary.selectedCount} of ${headSummary.lineCount} lines` : ""}</p>
        <p className="text-muted-foreground truncate" data-testid="pay-heads-note-lines">
          {headLines.filter((ln) => headSel[ln.demand_id]?.checked).map((ln) => `${ln.tax_head_name}${ln.fy_label ? ` ${ln.fy_label}` : ""} ${fmtINR(Number(headSel[ln.demand_id].amount) || 0)}`).join(" · ") || "nothing ticked"}
        </p>
      </div>
      <Button type="button" variant="outline" size="sm" className="h-9 shrink-0" onClick={() => changeHeads(stage)} disabled={busy} data-testid="pay-heads-change-button">Change</Button>
    </div>
  ) : null;

  const onPhotoPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      // Phase 130: the cheque photocopy is shrunk to a <= 200 KB JPEG before it is kept / sent
      setChequePhoto(await compressToDataUrl(f));
    } catch (err) {
      toast.error("Could not read that photo — please try again");
    }
  };

  const buildAllocs = () => Object.entries(allocations)
    .filter(([, v]) => Number(v) > 0)
    .map(([demand_id, amount]) => ({ demand_id, amount: Number(amount) }));

  const submitRecord = async (channel) => {
    const allocs = buildAllocs();
    if (totalPay <= 0) return toast.error("Enter the amount collected");
    if (allocs.length === 0) return toast.error("Enter at least one amount");
    if (channel === "manual" && !modeId) return toast.error("Select payment mode");
    if (channel === "manual" && isCheque) {
      if (!extra.cheque_number.trim()) return toast.error("Enter the cheque number");
      if (!extra.bank_name.trim()) return toast.error("Enter the issuing bank");
    }
    if (channel === "pos" && !extra.utr_number.trim()) return toast.error("Enter the PoS transaction / approval reference");
    if (isPartial && !partialConfirmed) return toast.error("Tick 'Yes, this is a partial payment' to continue");
    if (isPartial && !nextDate) return toast.error("This is a partial payment — select the preferred date for the balance");
    if (isExcess && !excessReason) return toast.error("Select a reason for the excess amount");
    if (isExcess && excessReason === "other" && excessComment.trim().length < 5) return toast.error("Write a comment for the excess amount (at least 5 characters)");
    setBusy(true);
    try {
      const r = await api.post(`/t/${code}/payments`, {
        property_id: property.id,
        allocations: allocs,
        amount: totalPay,                                   // Phase 190: what was handed over (may exceed the allocations)
        partial_confirmed: isPartial ? partialConfirmed : false,
        excess_reason: isExcess ? excessReason : null,
        excess_comment: isExcess && excessReason === "other" ? excessComment.trim() : null,
        payment_mode_id: channel === "pos" ? null : modeId,
        collection_channel: channel,
        cheque_number: extra.cheque_number || null,
        cheque_date: extra.cheque_date || null,
        cheque_collection_date: extra.cheque_collection_date || null,
        cheque_photo_base64: isCheque ? (chequePhoto || null) : null,
        dd_number: extra.dd_number || null,
        bank_name: extra.bank_name || null,
        transaction_ref: extra.transaction_ref || null,
        utr_number: extra.utr_number || null,
        pos_device_id: extra.pos_device_id || null,
        next_payment_date: isPartial ? nextDate : null,
      });
      const msg = r.data.is_partial
        ? `Partial payment recorded • ${fmtINR(r.data.outstanding_after)} outstanding — owner notified`
        : r.data.payment_kind === "excess"
          ? `Payment recorded • Receipt ${r.data.receipt.receipt_number} • ${fmtINR(r.data.excess_amount)} excess ${r.data.flag ? "flagged for review" : "held as advance credit"}`
          : `Payment recorded • Receipt ${r.data.receipt.receipt_number}`;
      toast.success(msg, r.data.flag ? { description: `${r.data.flag.reason_label}: the Supervisor and Municipal Admin can see it under Flagged Payments.` } : undefined);
      pokeLiveCollections();   // Phase 149: every open collection page in this tab refreshes right away
      setStage(null);
      if (r.data.receipt_visible === false) {
        setRecorded({ receiptNumber: r.data.receipt.receipt_number, amount: fmtINR(r.data.receipt?.total_amount ?? r.data.payment?.amount),
          outstandingAfter: r.data.outstanding_after, isPartial: r.data.is_partial, message: r.data.receipt_hidden_message });
        return;
      }
      navigate(`/${code}/receipt/${r.data.receipt.receipt_number}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const startOnline = async () => {
    const amt = Number(onlineAmount);
    if (!amt || amt <= 0) return toast.error("Enter the amount to collect online");
    if (importCheck?.blocked) return toast.error(importCheck.message);
    if (importCheck?.has_import && importCheck.remaining_payable != null && amt > importCheck.remaining_payable + 0.004) {
      return toast.error(`Only the remaining balance of ${fmtINR(importCheck.remaining_payable)} can be collected — a bulk import already covers the rest`);
    }
    setBusy(true);
    try {
      // Phase 229: with several tax heads the ticked lines go along as explicit allocations - only they are settled
      const payload = headsMode
        ? { property_id: property.id, amount: amt, allocations: summarizeSelection(headLines, headSel, headCaps).allocations }
        : { property_id: property.id, amount: amt };
      if (headsMode && payload.allocations.length === 0) { setBusy(false); return toast.error("Tick at least one tax head to pay"); }
      const { data } = await api.post(`/t/${code}/citizen/pay-online/order`, payload);
      const ok = await loadRazorpayScript();
      if (!ok) {
        toast.error("Could not load the payment gateway. Check the internet connection.");
        setBusy(false);
        return;
      }
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: data.name,
        description: data.description,
        order_id: data.order_id,
        prefill: data.prefill,
        handler: async (resp) => {
          try {
            const v = await api.post(`/t/${code}/citizen/pay-online/verify`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success(`Payment successful • Receipt ${v.data.receipt_number}`);
            pokeLiveCollections();   // Phase 149
            setStage(null);
            if (v.data.receipt_visible === false) {
              setRecorded({ receiptNumber: v.data.receipt_number, amount: fmtINR(v.data.amount ?? v.data.receipt?.total_amount), message: v.data.receipt_hidden_message });
              return;
            }
            navigate(`/${code}/receipt/${v.data.receipt_number}`);
          } catch (err) {
            toast.error(errMsg(err));
          }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.open();
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  /** Phase 190 — the master "Amount collected" field with its live partial / full / excess state.
   *  Phase 195 — a read-only "Demand Due" reference sits above it; the field starts blank and the collector must type the
   *  amount actually received (green = full, orange = partial, red = excess). */
  const amountTone = check.kind === "partial" ? "border-orange-500 ring-2 ring-orange-200 bg-orange-50/60 text-orange-950"
    : check.kind === "excess" ? "border-red-500 ring-2 ring-red-200 bg-red-50/60 text-red-950"
      : check.kind === "full" ? "border-emerald-500 ring-2 ring-emerald-200 bg-emerald-50/60 text-emerald-950" : "";
  const amountBlock = (
    <div className="space-y-2" data-testid="pay-amount-block" data-state={check.kind} data-heads-mode={headsMode ? "1" : "0"}>
      {headsNote}
      <div className="rounded-lg border bg-muted/40 px-3 py-2 flex items-center justify-between gap-3" role="note" aria-readonly="true"
        aria-label={`Demand due ${fmtINR(netPayableTotal)}`} data-testid="pay-demand-due-label">
        <span className="text-sm font-medium text-muted-foreground inline-flex items-center gap-1.5"><Receipt className="h-4 w-4" aria-hidden="true" /> Demand Due{headsMode ? " (all heads)" : ""}</span>
        <span className="text-lg font-semibold tabular-nums" data-testid="pay-payable-today">{fmtINR(netPayableTotal)}</span>
      </div>
      <Label htmlFor="pay-amount-input">Amount collected (₹) *</Label>
      <Input id="pay-amount-input" type="number" inputMode="decimal" min="0" step="0.01" value={amountStr} placeholder="Enter the amount received"
        onChange={(e) => onAmountChange(e.target.value)} className={`h-12 text-lg font-semibold tabular-nums ${amountTone} ${amountLocked ? "bg-muted/60" : ""}`}
        readOnly={amountLocked} aria-readonly={amountLocked}
        aria-invalid={check.kind === "excess"} aria-describedby="pay-amount-status" data-testid="pay-amount-input" />
      {amountLocked && (
        <p className="text-[11px] text-muted-foreground" data-testid="pay-amount-locked-hint">
          The amount is the total of the tax heads you ticked. Use <span className="font-medium">Change</span> above to pay other heads or different amounts.
        </p>
      )}
      {check.kind === "none" && (
        <p id="pay-amount-status" className="text-[11px] text-muted-foreground" data-testid="pay-status-none">
          Type the amount actually received from the citizen — it is compared with the demand due as you type. Nothing is pre-filled.
        </p>
      )}
      {check.kind === "partial" && (
        <div id="pay-amount-status" className="rounded-lg border border-orange-300 bg-orange-50/80 px-3 py-2.5 space-y-2.5" role="status" data-testid="pay-status-partial">
          <p className="text-[13px] text-orange-900 flex gap-2"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span data-testid="pay-status-message">
              {headsMode && headSummary.headsSelected < headSummary.headsTotal
                ? <>Only {headSummary.headsSelected} of {headSummary.headsTotal} tax heads are being paid ({fmtINR(totalPay)} of {fmtINR(netPayableTotal)} due on this property). The other heads stay due and this is recorded as a partial payment. Remaining balance: <span className="font-semibold" data-testid="partial-outstanding">{fmtINR(outstandingAfter)}</span>.</>
                : <>Amount entered ({fmtINR(totalPay)}) is less than the demand due ({fmtINR(netPayableTotal)}). This will be recorded as a partial payment. Remaining balance: <span className="font-semibold" data-testid="partial-outstanding">{fmtINR(outstandingAfter)}</span>.</>}
            </span></p>
          <label className="flex items-start gap-2.5 rounded-md bg-white/70 border border-orange-200 px-3 py-2 cursor-pointer" htmlFor="partial-confirm-checkbox">
            <Checkbox id="partial-confirm-checkbox" checked={partialConfirmed} onCheckedChange={(v) => setPartialConfirmed(!!v)}
              className="mt-0.5 border-orange-600 data-[state=checked]:bg-orange-600 data-[state=checked]:border-orange-600" data-testid="partial-confirm-checkbox" />
            <span className="text-sm font-medium text-orange-950">Yes, this is a partial payment</span>
          </label>
          <div className="space-y-1">
            <Label className="text-orange-900">Preferred date for balance payment *</Label>
            <Input type="date" className="h-11 bg-white" value={nextDate} onChange={(e) => setNextDate(e.target.value)}
              min={new Date().toISOString().slice(0, 10)} data-testid="partial-next-date-input" />
          </div>
          <p className="text-[11px] text-orange-800">The owner is notified by SMS / WhatsApp about the amount paid, the balance due, and this date.
            {quote?.totals?.discount > 0 ? " Partial payments forfeit the full-settlement discount." : ""}</p>
        </div>
      )}
      {check.kind === "full" && (
        <p id="pay-amount-status" className="rounded-lg border border-emerald-300 bg-emerald-50/80 px-3 py-2 text-[13px] text-emerald-900 flex gap-2" role="status" data-testid="pay-status-full">
          <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" /><span data-testid="pay-status-message">Full payment — amount matches the demand due.</span>
        </p>
      )}
      {check.kind === "excess" && (
        <div id="pay-amount-status" className="rounded-lg border border-red-300 bg-red-50/80 px-3 py-2.5 space-y-2.5" role="alert" data-testid="pay-status-excess">
          <p className="text-[13px] text-red-900 flex gap-2"><OctagonAlert className="h-4 w-4 shrink-0 mt-0.5" />
            <span data-testid="pay-status-message">Amount entered ({fmtINR(totalPay)}) exceeds the demand due ({fmtINR(netPayableTotal)}) by <span className="font-semibold" data-testid="excess-amount">{fmtINR(check.excess)}</span>. Select a reason before submitting.</span></p>
          <div className="space-y-1">
            <Label className="text-red-900">Reason for the excess *</Label>
            <Select value={excessReason} onValueChange={(v) => { setExcessReason(v); if (v !== "other") setExcessComment(""); }}>
              <SelectTrigger className="h-11 bg-white border-red-300" data-testid="excess-reason-select"><SelectValue placeholder="Select a reason" /></SelectTrigger>
              <SelectContent>
                {EXCESS_REASONS.map((r, i) => <SelectItem key={r.value} value={r.value} data-testid={`excess-reason-${r.value}`}>{i + 1}. {r.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {excessReason && <p className="text-[11px] text-red-800" data-testid="excess-reason-hint">{EXCESS_REASONS.find((r) => r.value === excessReason)?.hint}</p>}
          </div>
          {excessReason === "other" && (
            <div className="space-y-1">
              <Label className="text-red-900">Comment *</Label>
              <Textarea rows={2} className="bg-white border-red-300" value={excessComment} onChange={(e) => setExcessComment(e.target.value)}
                placeholder="Why is the owner paying more than the demand due?" data-testid="excess-comment-input" />
              {excessComment.trim().length > 0 && excessComment.trim().length < 5 && <p className="text-[11px] text-red-700">At least 5 characters.</p>}
            </div>
          )}
        </div>
      )}
      {!headsMode && (
      <Collapsible open={showAlloc} onOpenChange={setShowAlloc}>
        <CollapsibleTrigger asChild>
          <button type="button" className="text-[12px] text-muted-foreground inline-flex items-center gap-1 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring rounded" data-testid="pay-alloc-toggle">
            <SlidersHorizontal className="h-3.5 w-3.5" /> {showAlloc ? "Hide" : "Show"} how the amount is allocated across {pendingDemands.length} demand{pendingDemands.length === 1 ? "" : "s"}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2">
          <div className="space-y-2" data-testid="pay-allocation-list">
            {pendingDemands.map((d) => {
              const q = qmap[d.id];
              const maxPay = capFor(d, q);
              return (
                <div key={d.id} className="flex items-center gap-3 border rounded-lg px-3 py-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{d.tax_head_name} <span className="text-[11px] text-muted-foreground font-normal">{d.fy_label}</span></p>
                    <p className="text-[11px] text-muted-foreground">
                      Balance {fmtINR(d.balance_amount)}
                      {q?.penalty_new > 0 && <span className="text-amber-700"> • Penalty +{fmtINR(q.penalty_new)}</span>}
                      {q?.discount > 0 && <span className="text-emerald-700"> • Discount −{fmtINR(q.discount)} on full payment</span>}
                    </p>
                    {q && (q.penalty_new > 0 || q.discount > 0) && (
                      <p className="text-[11px] font-medium">Net payable {fmtINR(q.net_payable_full)}</p>
                    )}
                  </div>
                  <Input type="number" inputMode="decimal" className="w-28 h-10 text-right" value={allocations[d.id] ?? ""}
                    max={maxPay} onChange={(e) => onAllocChange(d, e.target.value)}
                    data-testid={`pay-amount-input-${d.tax_code || d.id}`} />
                </div>
              );
            })}
            {isExcess && <p className="text-[11px] text-red-800 tabular-nums">+ {fmtINR(check.excess)} above the demands → advance credit{excessReason && excessReason !== "advance_payment" ? " (flagged)" : ""}.</p>}
          </div>
        </CollapsibleContent>
      </Collapsible>
      )}
      {headsMode && isExcess && <p className="text-[11px] text-red-800 tabular-nums">+ {fmtINR(check.excess)} above the demands → advance credit{excessReason && excessReason !== "advance_payment" ? " (flagged)" : ""}.</p>}
    </div>
  );

  const QuoteSummary = () => !quote ? null : (
    <div className="rounded-lg border bg-accent/40 px-3 py-2 text-sm space-y-1" data-testid="pay-quote-summary">
      <div className="flex justify-between"><span className="text-muted-foreground">Total due</span><span className="tabular-nums">{fmtINR(quote.totals.due_with_penalty)}</span></div>
      <div className="flex justify-between"><span className="text-muted-foreground">Penalty</span><span className="tabular-nums text-amber-700" data-testid="pay-quote-penalty">{fmtINR(quote.totals.penalty)}</span></div>
      <div className="flex justify-between"><span className="text-muted-foreground">Discount (on full settlement)</span><span className="tabular-nums text-emerald-700" data-testid="pay-quote-discount">{fmtINR(quote.totals.discount)}</span></div>
      <div className="flex justify-between font-semibold border-t pt-1"><span>Net payable</span><span className="tabular-nums" data-testid="pay-quote-net">{fmtINR(quote.totals.net_payable_full)}</span></div>
      {quote.totals.discount > 0 && (
        <p className="text-[11px] text-muted-foreground">Discount applies only when a demand is settled in full — partial payments forfeit it.</p>
      )}
    </div>
  );

  return (
    <>
      <Button size="lg" className="h-12 w-full" disabled={disabled || !!importCheck?.blocked} onClick={() => (gate ? gate(open) : open())} data-testid="collector-collect-button">
        <IndianRupee className="h-4 w-4 mr-1" /> Collect Payment
      </Button>
      {/* Phase 169: payment confirmation when the receipt itself may not be opened by this role */}
      <PaymentRecordedDialog open={!!recorded} onClose={() => setRecorded(null)} {...(recorded || {})} testId="collector-payment-recorded" />

      {/* ---------- Channel chooser ---------- */}
      <Drawer open={stage === "choose"} onOpenChange={(o) => !o && setStage(null)}>
        <DrawerContent className="max-h-[92vh]">
          <DrawerHeader><DrawerTitle className="font-heading">How is the payment being collected?</DrawerTitle></DrawerHeader>
          <div className="px-4 pb-6 space-y-3">
            {CHANNELS.map((c) => (
              <button key={c.key} onClick={() => setStage(c.key === "online" ? "online" : c.key)}
                className="w-full flex items-center gap-3 border rounded-xl px-4 py-3.5 text-left hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring transition-colors"
                data-testid={c.testId}>
                <span className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <c.icon className="h-5 w-5" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium">{c.title}</span>
                  <span className="block text-xs text-muted-foreground">{c.desc}</span>
                </span>
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              </button>
            ))}
            <p className="text-[11px] text-muted-foreground text-center">
              {headsMode
                ? <>Paying {headSummary.headsSelected} of {headSummary.headsTotal} tax heads: <span className="font-medium text-foreground" data-testid="choose-heads-total">{fmtINR(headSummary.total)}</span> · <button type="button" className="text-primary underline-offset-2 hover:underline" onClick={() => changeHeads("choose")} data-testid="choose-heads-change">Change</button></>
                : <>Net payable: {fmtINR(netPayableTotal)}</>}
            </p>
          </div>
        </DrawerContent>
      </Drawer>

      {/* ---------- Phase 229: which tax heads / years are being paid? ---------- */}
      <Drawer open={stage === "heads"} onOpenChange={(o) => !o && setStage(null)}>
        <DrawerContent className="max-h-[92vh]">
          <DrawerHeader><DrawerTitle className="font-heading">Which tax heads are being paid?</DrawerTitle></DrawerHeader>
          <div className="px-4 pb-6 space-y-4 overflow-y-auto" data-testid="collect-heads-step">
            <ImportBlockNotice check={importCheck?.has_import ? importCheck : null} testId="heads-import-check" />
            <TaxHeadPicker lines={headLines} value={headSel} onChange={setHeadSel} caps={headCaps} disabled={busy} testIdPrefix="collect-heads" />
            <Button size="lg" className="w-full h-12" onClick={applyHeads} disabled={busy || headSummary.errors.length > 0 || headSummary.selectedCount === 0} data-testid="collect-heads-continue-button">
              {headSummary.selectedCount === 0 ? "Tick at least one tax head" : `Continue with ${fmtINR(headSummary.total)}`} <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
            <p className="text-[11px] text-muted-foreground text-center">Only the ticked heads / years are marked paid; everything else stays due on the property.</p>
          </div>
        </DrawerContent>
      </Drawer>

      {/* ---------- Manual record ---------- */}
      <Drawer open={stage === "manual"} onOpenChange={(o) => !o && setStage(null)}>
        <DrawerContent className="max-h-[92vh]">
          <DrawerHeader><DrawerTitle className="font-heading">Record manual collection</DrawerTitle></DrawerHeader>
          <div className="px-4 pb-6 space-y-4 overflow-y-auto">
            <ImportBlockNotice check={importCheck?.has_import ? importCheck : null} testId="manual-import-check" />
            {amountBlock}
            <QuoteSummary />
            <div className="space-y-1">
              <Label>Payment mode</Label>
              <Select value={modeId} onValueChange={setModeId}>
                <SelectTrigger className="h-11" data-testid="collector-payment-mode-select"><SelectValue placeholder="Select mode" /></SelectTrigger>
                <SelectContent>
                  {modes.map((m) => <SelectItem key={m.id} value={m.id} data-testid={`payment-mode-${m.mode_code}`}>{m.mode_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {isCheque && (
              <div className="rounded-lg border px-3 py-3 space-y-2" data-testid="cheque-details-block">
                <p className="text-sm font-medium">Cheque details</p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1"><Label className="text-xs">Cheque number *</Label>
                    <Input className="h-10" value={extra.cheque_number} onChange={(e) => setExtra({ ...extra, cheque_number: e.target.value })} data-testid="cheque-number-input" /></div>
                  <div className="space-y-1"><Label className="text-xs">Date on cheque</Label>
                    <Input type="date" className="h-10" value={extra.cheque_date} onChange={(e) => setExtra({ ...extra, cheque_date: e.target.value })} data-testid="cheque-date-input" /></div>
                  <div className="space-y-1 col-span-2"><Label className="text-xs">Issuing bank *</Label>
                    <Input className="h-10" value={extra.bank_name} onChange={(e) => setExtra({ ...extra, bank_name: e.target.value })} placeholder="e.g. State Bank of India" data-testid="cheque-bank-input" /></div>
                  <div className="space-y-1"><Label className="text-xs">Date of collection</Label>
                    <Input type="date" className="h-10" value={extra.cheque_collection_date}
                      onChange={(e) => setExtra({ ...extra, cheque_collection_date: e.target.value })} data-testid="cheque-collection-date-input" /></div>
                  <div className="space-y-1"><Label className="text-xs">Cheque amount</Label>
                    <div className="h-10 border rounded-md flex items-center px-3 text-sm bg-muted tabular-nums" data-testid="cheque-amount-display">{hasAmount ? fmtINR(totalPay) : "—"}</div></div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Cheque photocopy</Label>
                  {chequePhoto ? (
                    <div className="relative">
                      <img src={chequePhoto} alt="Cheque" className="w-full max-h-40 object-contain rounded-md border bg-white" data-testid="cheque-photo-preview" />
                      <Button type="button" variant="secondary" size="icon" className="absolute top-1 right-1 h-7 w-7"
                        onClick={() => setChequePhoto("")} data-testid="cheque-photo-remove"><X className="h-4 w-4" /></Button>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center gap-2 border border-dashed rounded-md h-16 text-sm text-muted-foreground cursor-pointer hover:bg-accent transition-colors" data-testid="cheque-photo-upload">
                      <ImageUp className="h-4 w-4" /> Upload / photograph the cheque
                      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhotoPick} />
                    </label>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground">The supervisor will deposit this cheque at the bank and update its credit status.</p>
              </div>
            )}
            {selectedMode?.mode_code === "DD" && (
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="DD number" value={extra.dd_number} onChange={(e) => setExtra({ ...extra, dd_number: e.target.value })} />
                <Input placeholder="Bank name" value={extra.bank_name} onChange={(e) => setExtra({ ...extra, bank_name: e.target.value })} />
              </div>
            )}
            {!isCheque && wantsTxnRef && (
              <div className="space-y-1">
                <Label>UTR / Transaction reference</Label>
                <Input placeholder="UTR number or transaction ID" value={extra.utr_number}
                  onChange={(e) => setExtra({ ...extra, utr_number: e.target.value })} data-testid="utr-number-input" />
              </div>
            )}
            {!canSubmit && !busy && (
              <p className="text-[11px] text-muted-foreground text-center" data-testid="pay-submit-hint">
                {!hasAmount ? "Enter the amount received to enable Submit." : isPartial && !partialConfirmed ? "Tick “Yes, this is a partial payment” to enable Submit." : isPartial && !nextDate ? "Select the preferred date for the balance." : isExcess && !excessReason ? "Select a reason for the excess amount to enable Submit." : isExcess ? "Write a comment (at least 5 characters) to enable Submit." : ""}
              </p>
            )}
            <Button size="lg" className="w-full h-12" onClick={() => submitRecord("manual")} disabled={!canSubmit} data-testid="collector-confirm-payment-button">
              {busy ? "Recording..." : !hasAmount ? "Submit" : `Submit ${fmtINR(totalPay)}${isPartial ? " (partial)" : isExcess ? " (incl. excess)" : ""}`}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>

      {/* ---------- PoS device ---------- */}
      <Drawer open={stage === "pos"} onOpenChange={(o) => !o && setStage(null)}>
        <DrawerContent className="max-h-[92vh]">
          <DrawerHeader><DrawerTitle className="font-heading">PoS device collection</DrawerTitle></DrawerHeader>
          <div className="px-4 pb-6 space-y-4 overflow-y-auto">
            <p className="text-xs text-muted-foreground -mt-2">Swipe the card / scan UPI on the handheld PoS machine, then record the transaction reference here.</p>
            <ImportBlockNotice check={importCheck?.has_import ? importCheck : null} testId="pos-import-check" />
            {amountBlock}
            <QuoteSummary />
            <div className="grid grid-cols-1 gap-2">
              <div className="space-y-1">
                <Label>PoS transaction / approval reference *</Label>
                <Input placeholder="e.g. RRN / approval code from the PoS slip" value={extra.utr_number}
                  onChange={(e) => setExtra({ ...extra, utr_number: e.target.value })} data-testid="pos-txn-ref-input" />
              </div>
              <div className="space-y-1">
                <Label>PoS device ID (optional)</Label>
                <Input placeholder="Terminal ID printed on the device" value={extra.pos_device_id}
                  onChange={(e) => setExtra({ ...extra, pos_device_id: e.target.value })} data-testid="pos-device-id-input" />
              </div>
            </div>
            {!canSubmit && !busy && (
              <p className="text-[11px] text-muted-foreground text-center" data-testid="pos-submit-hint">
                {!hasAmount ? "Enter the amount received to enable Submit." : isPartial && !partialConfirmed ? "Tick “Yes, this is a partial payment” to enable Submit." : isPartial && !nextDate ? "Select the preferred date for the balance." : isExcess && !excessReason ? "Select a reason for the excess amount to enable Submit." : isExcess ? "Write a comment (at least 5 characters) to enable Submit." : ""}
              </p>
            )}
            <Button size="lg" className="w-full h-12" onClick={() => submitRecord("pos")} disabled={!canSubmit} data-testid="pos-confirm-payment-button">
              {busy ? "Recording..." : !hasAmount ? "Submit via PoS" : `Submit ${fmtINR(totalPay)} via PoS${isPartial ? " (partial)" : isExcess ? " (incl. excess)" : ""}`}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>

      {/* ---------- Online gateway ---------- */}
      <Drawer open={stage === "online"} onOpenChange={(o) => !o && setStage(null)}>
        <DrawerContent className="max-h-[92vh]">
          <DrawerHeader><DrawerTitle className="font-heading">Online payment (gateway)</DrawerTitle></DrawerHeader>
          <div className="px-4 pb-6 space-y-4 overflow-y-auto">
            {headsNote}
            <QuoteSummary />
            <div className="space-y-1">
              <Label>Amount to collect</Label>
              <Input type="number" inputMode="decimal" readOnly={headsMode} aria-readonly={headsMode} className={`h-11 ${headsMode ? "bg-muted/60" : ""} ${(() => { const k = classifyAmount(onlineAmount, netPayableTotal).kind; return k === "partial" ? "border-amber-500 ring-2 ring-amber-200" : k === "excess" ? "border-red-500 ring-2 ring-red-200" : k === "full" ? "border-emerald-500 ring-2 ring-emerald-200" : ""; })()}`} value={onlineAmount}
                onChange={(e) => setOnlineAmount(e.target.value)} data-testid="online-amount-input" />
              {headsMode && <p className="text-[11px] text-muted-foreground" data-testid="online-amount-locked-hint">The amount is the total of the tax heads you ticked — use <span className="font-medium">Change</span> above to adjust it.</p>}
              {(() => {   // Phase 190: the same live check; the gateway cannot carry an excess, so it is simply refused here
                const c = classifyAmount(onlineAmount, netPayableTotal);
                if (c.kind === "partial") return <p className="text-[11px] text-amber-800" data-testid="online-status-partial">Amount entered ({fmtINR(Number(onlineAmount) || 0)}) is less than total demand due ({fmtINR(netPayableTotal)}). This will be recorded as a partial payment. Remaining balance: {fmtINR(c.remaining)}.</p>;
                if (c.kind === "full") return <p className="text-[11px] text-emerald-800" data-testid="online-status-full">Full payment — amount matches demand due.</p>;
                if (c.kind === "excess") return <p className="text-[11px] text-red-800" data-testid="online-status-excess">Amount exceeds total demand due ({fmtINR(netPayableTotal)}) by {fmtINR(c.excess)} — online payments cannot exceed the amount due. Use Manual collection to record an excess with a reason.</p>;
                return <p className="text-[11px] text-muted-foreground">Defaults to the full net payable. A lower amount is treated as a partial payment.</p>;
              })()}
            </div>
            <Button size="lg" className="w-full h-12" onClick={startOnline} disabled={busy || classifyAmount(onlineAmount, netPayableTotal).kind === "excess"} data-testid="online-pay-button">
              <Globe className="h-4 w-4 mr-2" /> {busy ? "Opening secure checkout..." : `Collect ${fmtINR(Number(onlineAmount) || 0)} online`}
            </Button>
            <p className="text-[11px] text-muted-foreground text-center">Secured by Razorpay • UPI, cards, net banking supported</p>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
};
